// useClientesListXL - Sprint 44 Fase 3 Vista 2.
// Version moderna del hook de lista de clientes con:
//   - Paginacion real (page/perPage + setPage).
//   - Auto-dismiss de msg tras 4 segundos (fix deuda UX legacy).
//   - Debounce de `q` delegado al consumidor (XLInput + setTimeout en la vista).
//
// Reusa el service existente src/modules/clientes/services/clientesService.js
// sin modificar. Contrato backend intacto.
import { useCallback, useEffect, useRef, useState } from 'react';
import * as svc from '../../clientes/services/clientesService';

const DEFAULT_PER_PAGE = 25;
const MSG_AUTO_DISMISS_MS = 4000;

export default function useClientesListXL() {
  const [items, setItems]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState(null);

  const [page, setPage]     = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [q, setQ]           = useState('');
  const [pais, setPais]     = useState('');
  const [activo, setActivo] = useState('true');
  const [msg, setMsg]       = useState(null);

  const msgTimer = useRef(null);

  const pushMsg = useCallback((kind, text) => {
    if (msgTimer.current) clearTimeout(msgTimer.current);
    setMsg({ kind, text });
    msgTimer.current = setTimeout(() => setMsg(null), MSG_AUTO_DISMISS_MS);
  }, []);

  useEffect(() => () => {
    if (msgTimer.current) clearTimeout(msgTimer.current);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await svc.listClientes({ page, per_page: perPage, q, pais, activo });
      setItems(r.items);
      setTotal(r.total);
    } catch (e) {
      setErr((e && e.message) || 'Error al listar clientes');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, q, pais, activo]);

  useEffect(() => { load(); }, [load]);

  // Reset page a 1 al cambiar filtros (no al cambiar page).
  const resetAndSetQ     = useCallback((v) => { setPage(1); setQ(v); }, []);
  const resetAndSetPais  = useCallback((v) => { setPage(1); setPais(v); }, []);
  const resetAndSetActivo= useCallback((v) => { setPage(1); setActivo(v); }, []);

  const deactivate = useCallback(async (id) => {
    try {
      await svc.deactivateCliente(id);
      await load();
      pushMsg('ok', 'Cliente inactivado');
    } catch (e) {
      pushMsg('err', (e && e.message) || 'Error al inactivar');
    }
  }, [load, pushMsg]);

  const activate = useCallback(async (id) => {
    try {
      await svc.activateCliente(id);
      await load();
      pushMsg('ok', 'Cliente activado');
    } catch (e) {
      pushMsg('err', (e && e.message) || 'Error al activar');
    }
  }, [load, pushMsg]);

  const totalPages = Math.max(1, Math.ceil((total || 0) / (perPage || 1)));
  const canPrev = page > 1;
  const canNext = page < totalPages;

  return {
    items, total, loading, err,
    page, perPage, setPage, setPerPage,
    totalPages, canPrev, canNext,
    q, setQ: resetAndSetQ,
    pais, setPais: resetAndSetPais,
    activo, setActivo: resetAndSetActivo,
    msg, pushMsg, clearMsg: () => setMsg(null),
    load, deactivate, activate,
  };
}
